import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Dayjs } from 'dayjs';
import { getDayjsLocale, getMuiDatePickerLocaleText } from 'utils/datePickerLocale';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: CRA does not generate types for raw SCSS files natively.
import themeVars from 'styles/_variables.scss';

export { getDayjsLocale, getMuiDatePickerLocaleText };

interface CustomDatePickerProps {
  selected: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  label?: string;
}

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: themeVars.accentPink || '#d80f83',
    },
    background: {
      paper: themeVars.bgDropdown || '#333c4f',
      default: themeVars.bgBase || '#0f1923',
    },
    text: {
      primary: themeVars.textPrimary || '#ffffff',
      secondary: themeVars.textSecondary || '#8698a6',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: themeVars.bgDropdown || '#333c4f',
          backgroundImage: 'none',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '15px',
          transform: 'translate(14px, 12px) scale(1)',
          color: themeVars.textSecondary || '#8698a6',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 6,
          color: '#e4e7ed',
          fieldset: {
            borderColor: 'rgba(255, 255, 255, 0.1)',
            transition: 'border-color 0.2s',
          },
          '&:hover fieldset': {
            borderColor: themeVars.accentPink || '#d80f83',
          },
          '&.Mui-focused fieldset': {
            borderColor: themeVars.accentPink || '#d80f83',
          },
        },
        input: {
          padding: '4px 12px',
          fontSize: '15px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#8f9fb2',
        },
      },
    },
  },
});

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  selected,
  onChange,
  minDate,
  maxDate,
  label,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const effectiveLabel = label ?? t('datePicker.label');

  return (
    <ThemeProvider theme={darkTheme}>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={getDayjsLocale(currentLang)}
        localeText={getMuiDatePickerLocaleText(currentLang)}
      >
        <DateTimePicker
          label={effectiveLabel}
          value={selected}
          onChange={onChange}
          minDateTime={minDate || undefined}
          maxDateTime={maxDate || undefined}
          sx={{
            width: '100%',
            '& .MuiPickersSectionList-root': {
              paddingTop: '10px !important',
              paddingBottom: '10px !important',
            },
          }}
          slotProps={{
            popper: {
              disablePortal: true,
              popperOptions: {
                strategy: 'fixed',
              },
              sx: {
                zIndex: 100000,
                '& .MuiPaper-root': {
                  transform: 'translateY(-15px)',
                },
              },
              modifiers: [
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: false,
                    rootBoundary: 'document',
                  },
                },
                {
                  name: 'flip',
                  enabled: true,
                  options: {
                    fallbackPlacements: ['top-start', 'bottom-start', 'right', 'left'],
                  },
                },
              ],
            },
          }}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default CustomDatePicker;
